// src/pages/ShippingPolicy.js
// Shipping policy — delivery times, costs, tracking, and customs info.

import React from 'react';
import usePageTitle from '../hooks/usePageTitle';

const ShippingPolicy = () => {
    usePageTitle('Shipping Policy', 'MAISON D\'OUD shipping information. Free standard delivery on all orders to Ireland, EU and UK.');

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-light text-foreground mb-2 tracking-wide">Shipping Policy</h1>
                <p className="text-muted-foreground text-sm mb-12">Last updated: April 23, 2026</p>

                <div className="space-y-10 text-muted-foreground leading-relaxed">

                    <section>
                        <p>We want your order to reach you as quickly and smoothly as possible. Below you'll find everything you need to know about our shipping options, delivery times and costs.</p>
                        <p className="mt-4">All orders include free standard delivery. If you'd prefer a faster option, express delivery is available at checkout for a flat rate of €9.99, regardless of whether you're in Ireland or the EU.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Ireland</h2>
                        <p>Standard delivery across Ireland is free and typically takes 3 to 7 working days. If you need your order sooner, express delivery is available for a flat rate of €9.99 and usually arrives within 1 to 3 working days, provided you place your order before 5PM GMT.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">European Union</h2>
                        <p>Standard delivery to the EU is free, with an estimated delivery time of 5 to 9 working days. Express delivery is available for a flat rate of €9.99 and typically takes 3 to 5 working days.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">United Kingdom</h2>
                        <p>Standard delivery to the UK is free, with an estimated delivery time of 5 to 9 working days. Express delivery is available for €9.99 and typically takes 3 to 5 working days.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Order Tracking</h2>
                        <p>Once your order has been dispatched, you'll receive a tracking number by email so you can keep an eye on your delivery. If you haven't received your tracking details within 2 working days of placing your order, please check your spam folder or get in touch with us.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Delays</h2>
                        <p>While we do our best to meet all estimated delivery windows, delays can occasionally happen, particularly during busy periods, public holidays or due to customs clearance for international orders. These are sometimes outside our control, and we appreciate your patience when they occur.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">International Orders and Customs</h2>
                        <p>For orders outside Ireland, the EU and the UK, delivery times and costs will be calculated at checkout. Please be aware that your order may be subject to import duties or taxes once it reaches its destination country. These charges are the responsibility of the customer and are not included in our shipping rates.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Delivery Address</h2>
                        <p>Please make sure your delivery address is correct before completing your order. Once an order has been dispatched, we're unable to redirect it. We won't be held responsible for orders delivered to an incorrect address provided at checkout.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Missing or Late Orders</h2>
                        <p>If your order hasn't arrived within 2 to 3 days of the expected delivery date, please reach out to us at <a href="mailto:support@maisondoud.com" className="text-[#CEAC8F] hover:underline">support@maisondoud.com</a> and we'll look into it straight away.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Signature on Delivery</h2>
                        <p>Depending on your location and the delivery option selected, a signature may be required upon delivery. If you're not available when the delivery is attempted, the carrier will leave instructions on how to arrange a redelivery or collection.</p>
                    </section>

                    <section>
                        <h2 className="text-xl font-medium text-foreground mb-4">Get in Touch</h2>
                        <p>If you have any questions about your delivery or need help with an order, our team is happy to help. Reach us at <a href="mailto:support@maisondoud.com" className="text-[#CEAC8F] hover:underline">support@maisondoud.com</a> or call us on +353 85 124 4472.</p>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default ShippingPolicy;